import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { leadingZeroFormatter, LOW_VALUE, MUTED_VALUE, SVGS, VOLUME_LEVELS } from 'components/VideoPlayer';
import { totalSeconds } from 'services/utils';

import './style.css';

const AudioPlayer = ({ src }) => {
	const [paused, setPaused] = useState(true);
	const [volumeLevel, setVolumeLevel] = useState(VOLUME_LEVELS.LOW);
	const [volume, setVolume] = useState(LOW_VALUE);
	const [currentTime, setCurrentTime] = useState(`0:${leadingZeroFormatter.format(0)}`);
	const [totalTime, setTotalTime] = useState(`0:${leadingZeroFormatter.format(0)}`);
	const [timeLine, setTimeLine] = useState(0);
	const [buffered, setBuffered] = useState(0);
	const [isScrubbing, setIsScrubbing] = useState(false);

	const audioRef = useRef();
	const timeLineRef = useRef();

	const className = classNames('AudioPlayer', { paused: paused });

	const _togglePlayAudio = e => {
		e?.preventDefault();
		setPaused(prev => !prev);
		if (paused) {
			audioRef.current?.play();
			return;
		}
		audioRef.current?.pause();
	};

	const _handleAudioBuffering = ({ target }) => {
		if (target.buffered.length <= 0) return;
		const bufferedTime = target.buffered.end(target.buffered.length - 1);
		let formattedBufferedTime = durationFormat(bufferedTime);
		formattedBufferedTime = ((100 * totalSeconds(formattedBufferedTime)) / totalSeconds(totalTime)).toFixed(2);
		setBuffered(formattedBufferedTime);
	};

	//mute and unmute
	const _toggleMute = e => {
		e?.preventDefault();
		if (volume <= MUTED_VALUE) {
			setVolume(0.5);
			return;
		}
		setVolume(0.0);
	};

	//handle volume change
	const _handleVolume = ({ target }) => {
		let value = target.value;
		if (value < MUTED_VALUE) {
			value = 0;
		}
		setVolume(value);
	};
	const durationFormat = time => {
		const seconds = Math.floor(time % 60);
		const minutes = Math.floor(time / 60) % 60;
		const hours = Math.floor(time / 3600);
		if (hours === 0) {
			return `${minutes}:${leadingZeroFormatter.format(seconds)}`;
		}
		return `${hours}:${leadingZeroFormatter.format(minutes)}:${leadingZeroFormatter.format(seconds)}`;
	};

	const _updateTimeline = e => {
		const parent = timeLineRef.current.getBoundingClientRect();
		const percentage = Math.min(Math.max(0, e.clientX - parent.x), parent.width) / parent.width;
		const newTimeLine = percentage * audioRef.current.duration;
		audioRef.current.currentTime = newTimeLine;
	};

	//timeline and video time
	const _handleTimeLineOnPointerDown = e => {
		setIsScrubbing(true);
		audioRef.current?.pause();
		_updateTimeline(e);
	};

	//audio on load
	const _handleAudioLoad = ({ target }) => {
		const time = durationFormat(target?.duration || 0);
		setTotalTime(time);
	};

	const _handleTimeLineOnPointerMove = e => {
		if (!isScrubbing) return;
		_updateTimeline(e);
	};

	const _handleTimeLineOnPointerUp = e => {
		_updateTimeline(e);
		setIsScrubbing(false);
		setPaused(false);
		audioRef.current?.play();
	};

	//update video , progressbar and buffering
	const _handleAudioTimeUpdate = ({ target }) => {
		let currTime = target.currentTime;
		currTime = durationFormat(currTime || 0);

		const time = durationFormat(target?.duration || 0);
		const newCurrTime = ((100 * totalSeconds(currTime)) / totalSeconds(time) || 0).toFixed(2);
		setCurrentTime(currTime);
		setTimeLine(newCurrTime || 0);
	};

	// volume change icon handler
	useEffect(() => {
		if (!audioRef?.current) return;
		audioRef.current.volume = volume;
		if (volume < MUTED_VALUE) {
			setVolumeLevel(VOLUME_LEVELS.MUTED);
			audioRef.current.muted = true;
			return;
		}
		audioRef.current.muted = false;
		if (volume >= MUTED_VALUE && volume < LOW_VALUE) setVolumeLevel(VOLUME_LEVELS.LOW);
		else setVolumeLevel(VOLUME_LEVELS.HIGH);
	}, [volume]);

	useEffect(() => {
		setPaused(true);
		setTimeLine(0);
		setBuffered(0);
	}, [src]);

	return (
		<div className={className} data-volume-level={volumeLevel}>
			<div className="controls">
				<button className="play-pause-btn" onClick={_togglePlayAudio}>
					<SVGS name="play-icon" />
					<SVGS name="pause-icon" />
				</button>
				<div className="timeline-container">
					<input
						ref={timeLineRef}
						type="range"
						readOnly
						step="any"
						value={timeLine}
						onPointerDown={_handleTimeLineOnPointerDown}
						onPointerMove={_handleTimeLineOnPointerMove}
						onPointerUp={_handleTimeLineOnPointerUp}
						className="timeline"
						style={{
							'--progress-position': timeLine,
							'--buffered-position': buffered,
						}}
					/>
				</div>

				<div className="duration-container">
					<div className="current-time">{currentTime}</div>
					<span>/</span>
					<div className="total-time">{totalTime}</div>
				</div>
				<div className="volume-container">
					<button className="mute-btn" onClick={_toggleMute}>
						<SVGS name="volume-high-icon" />
						<SVGS name="volume-low-icon" />
						<SVGS name="volume-muted-icon" />
					</button>
					<input
						className="volume-slider"
						type="range"
						min="0"
						max="1"
						step="0.01"
						value={volume}
						onChange={_handleVolume}
						onProgress={_handleAudioBuffering}
						style={{ '--volume-number': volume }}
					/>
				</div>
			</div>
			<audio
				ref={audioRef}
				src={src}
				onLoadedMetadata={_handleAudioLoad}
				onTimeUpdate={_handleAudioTimeUpdate}
				onClick={_togglePlayAudio}
				onProgress={_handleAudioBuffering}
				onEnded={_togglePlayAudio}
			/>
		</div>
	);
};
export default AudioPlayer;
